import React, {useContext, useEffect, useState} from 'react';
import {NavLink, useLocation} from "react-router-dom";
import {useAdminRoutes, useHelpAndLegalRoutes, useProjectRoutes} from "../../Routing.jsx";
import {Trans, useTranslation} from "react-i18next";
import SidebarLinkStandalone from "./links/SidebarLinkStandalone.jsx";
import SidebarLinkGroup from "./links/SidebarLinkGroup.jsx";
import Modal from "../Modal.jsx";
import {AdministrationApi} from "../../api/authority/generated/index.ts";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {canLoadPendingVatReviewCount} from "./pendingVatReviewCount.js";


function convertRoutes(
    routes,
    pathname,
    setSidebarExpanded,
    onLockedClick,
) {
    let groupedRoutes = [];

    for (let route of routes) {
        if (route['hiddenFromMenu']) {
            continue;
        }

        let group = route['group'] ?? null;

        route['is_current'] = (pathname === route['path']);

        if (group === null) {
            route['is_group'] = false;
            groupedRoutes[route['name'] + route['path']] = route;
        } else {
            let groupData = groupedRoutes[group];
            if (groupData === undefined) {
                groupData = {
                    is_group: true,
                    is_current: route['is_current'],
                    name: group,
                    icon: route.icon,
                    routes: []
                };
            }

            groupData.routes.push(route);
            groupData['is_current'] = groupData['is_current'] || route['is_current'];
            groupedRoutes[group] = groupData;
        }
    }

    let linksAndGroups = [];

    for (let routeOrGroupKey in groupedRoutes) {
        let routeOrGroup = groupedRoutes[routeOrGroupKey];
        if (routeOrGroup['is_group']) {
            linksAndGroups.push(
                <SidebarLinkGroup
                    key={routeOrGroup['name'] + routeOrGroup['path']}
                    group={routeOrGroup}
                    setSidebarExpanded={setSidebarExpanded}
                    onLockedClick={onLockedClick}
                />
            );
        } else {
            linksAndGroups.push(
                <SidebarLinkStandalone
                    key={routeOrGroup['name'] + routeOrGroup['path']}
                    route={routeOrGroup}
                    setSidebarExpanded={setSidebarExpanded}
                    onLockedClick={onLockedClick}
                />
            );
        }
    }

    return linksAndGroups;
}

export default function SidebarLinks({setSidebarExpanded}) {

    const location = useLocation();
    const {pathname} = location;

    let {t} = useTranslation();
    const {context, getAuthorityConfiguration} = useContext(GlobalContext);
    const [lockedRoute, setLockedRoute] = useState(null);
    const [pendingVatReviewCount, setPendingVatReviewCount] = useState(0);
    const loadPendingVatReviewCountAllowed = canLoadPendingVatReviewCount(context);
    const identityToken = context.currentIdentity?.token ?? null;

    useEffect(() => {
        setPendingVatReviewCount(0);
        if (!loadPendingVatReviewCountAllowed) return undefined;

        let cancelled = false;
        const loadPendingVatReviewCount = async () => {
            try {
                const result = await new AdministrationApi(getAuthorityConfiguration()).getAdminPendingSubscriptionVATReviewCount();
                if (!cancelled) setPendingVatReviewCount(result.count ?? 0);
            } catch {
                if (!cancelled) setPendingVatReviewCount(0);
            }
        };
        const updatePendingVatReviewCount = (event) => setPendingVatReviewCount(event.detail ?? 0);

        void loadPendingVatReviewCount();
        window.addEventListener("admin-subscription-vat-review-count-changed", updatePendingVatReviewCount);
        return () => {
            cancelled = true;
            window.removeEventListener("admin-subscription-vat-review-count-changed", updatePendingVatReviewCount);
        };
    }, [getAuthorityConfiguration, identityToken, loadPendingVatReviewCountAllowed]);

    let [projectRoutes, lastProjectName] = useProjectRoutes(true);
    let projectLinks = convertRoutes(projectRoutes, pathname, setSidebarExpanded, setLockedRoute);

    let [helpAndLegalRoutes] = useHelpAndLegalRoutes();
    let helpAndLegalLinks = convertRoutes(helpAndLegalRoutes, pathname, setSidebarExpanded, setLockedRoute);

    let [administrationRoutes] = useAdminRoutes();
    administrationRoutes = administrationRoutes.map((route) => route.path === "/admin/subscription-payments"
        ? {...route, badgeCount: pendingVatReviewCount}
        : route,
    );
    let adminLinks = convertRoutes(administrationRoutes, pathname, setSidebarExpanded, setLockedRoute);
    const projectId = lockedRoute?.path?.match(/^\/project\/([^/]+)/)?.[1];

    // noinspection com.intellij.reactbuddy.ArrayToJSXMapInspection
    return (
        <>
        <div className="space-y-8 d-none">
            <div>
                <h3 className="group-title text-xs uppercase text-gray-400 dark:text-gray-500 font-semibold pl-3">
                    <span className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                        {lastProjectName}
                    </span>
                </h3>

                <ul className="sidebar-links mt-3">
                    {projectLinks}
                </ul>
            </div>

            {helpAndLegalLinks.length > 0 &&
                <div>
                    <h3 className="group-title text-xs uppercase text-gray-400 dark:text-gray-500 font-semibold pl-3">
                        <span className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                            {t('route.support')}
                        </span>
                    </h3>

                    <ul className="sidebar-links mt-3">
                        {helpAndLegalLinks}
                    </ul>
                </div>
            }

            {adminLinks.length > 0 &&
                <div>
                    <ul className="sidebar-links">
                        {adminLinks}
                    </ul>
                </div>
            }
        </div>
        <Modal
            className="max-w-lg"
            visible={lockedRoute !== null}
            setVisible={(visible) => { if (!visible) setLockedRoute(null); }}
            title={t("subscription.feature_locked_title", {defaultValue: "Funkce není součástí vašeho balíčku"})}
        >
            <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                <Trans
                    i18nKey="subscription.feature_locked_description"
                    defaults="Funkce <strong>{{feature}}</strong> není dostupná v aktuálním balíčku. Podívejte se na vyšší balíčky a zvolte ten, který odpovídá potřebám projektu."
                    values={{feature: lockedRoute?.name}}
                    components={{strong: <strong className="font-semibold text-slate-800 dark:text-slate-100"/>}}
                />
            </p>
            {projectId && <div className="mt-5 flex justify-end">
                <NavLink
                    className="btn bg-violet-600 !text-white hover:bg-violet-700 hover:!text-white focus:!text-white"
                    to={`/project/${projectId}/settings/subscription`}
                    onClick={() => setLockedRoute(null)}
                >
                    {t("subscription.feature_locked_cta", {defaultValue: "Zobrazit balíčky"})}
                </NavLink>
            </div>}
        </Modal>
        </>
    );
}
